import React, { useEffect, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { toast } from "sonner";
import {
  ArrowLeft,
  KeyRound,
  CheckCircle2,
  Mail,
  AlertTriangle,
  Store,
  UserCheck,
  RotateCcw,
} from "lucide-react";
import api, { formatApiErrorDetail } from "../../lib/api";
import PageHeader from "../../components/system/PageHeader";
import Card from "../../components/system/Card";
import Button from "../../components/system/Button";
import Input, { Select } from "../../components/system/Input";
import UserStatusBadge from "../../components/system/UserStatusBadge";

/**
 * InviteUser flow — operator-only. Route stays at
 * ``/platform/companies/:cid/activate-admin`` for back-compat (legacy
 * bookmarks and the existing TenantDetail CTA), but the UX is now
 * generalised to invite ANY tenant role (admin / manager / employee).
 *
 * Behaviour:
 *   1. Form to create a tenant user
 *      (``POST /platform/companies/{cid}/users``).
 *      - Default role: ``employee``.
 *      - Optional ``active`` toggle, optional 500-char note.
 *   2. On success, immediately mint a single-use setup link
 *      (``POST /platform/companies/{cid}/admin-password-reset``) and
 *      reveal it so the operator can copy / re-email out-of-band.
 *      When the backend says ``purpose=invite`` we relabel everything
 *      as "Invite link" / "Set password" — the email body also uses
 *      the welcome template instead of the reset template.
 *   3. If the email is already in the SAME tenant, we skip create and
 *      switch to an "Existing user" panel that lets the operator
 *      generate a fresh setup link for that user. Any prior unused
 *      links are superseded server-side. Cross-tenant collisions
 *      keep the v1 guidance card (unchanged).
 */
export default function ActivateAdminFlow() {
  const { cid } = useParams();
  const nav = useNavigate();

  const [companyName, setCompanyName] = useState("");
  const [stores, setStores] = useState([]);
  const [departments, setDepartments] = useState([]);

  const [email, setEmail] = useState("");
  const [name, setName] = useState("");
  const [role, setRole] = useState("employee");
  const [storeId, setStoreId] = useState("");
  const [departmentId, setDepartmentId] = useState("");
  const [active, setActive] = useState(true);
  const [note, setNote] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const [createdUser, setCreatedUser] = useState(null);
  const [resetResult, setResetResult] = useState(null);
  const [minting, setMinting] = useState(false);
  // Structured 409 from /platform/companies/{cid}/users when email is
  // already a user of another tenant. Populated by the backend; lets
  // us render the v1 guidance callout below the form.
  const [duplicateEmail, setDuplicateEmail] = useState(null);
  // Same-tenant existing user (also 409 from backend, but with the
  // ``existing_user`` block populated). We render a panel that lets
  // the operator generate a fresh setup link for that user instead.
  const [existingUser, setExistingUser] = useState(null);

  // Preload tenant context (so we can show its name and offer
  // store/department dropdowns).
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const [det, st, dp] = await Promise.all([
          api.get(`/platform/companies/${cid}`),
          api.get(`/platform/companies/${cid}/stores`),
          api.get(`/platform/companies/${cid}/departments`),
        ]);
        if (cancelled) return;
        setCompanyName(det.data.company?.name || "");
        setStores(st.data.stores || []);
        setDepartments(dp.data.departments || []);
      } catch (e) {
        toast.error(formatApiErrorDetail(e));
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [cid]);

  const resetCollisionState = () => {
    setDuplicateEmail(null);
    setExistingUser(null);
  };

  const createUser = async (e) => {
    e?.preventDefault?.();
    const cleanEmail = email.trim().toLowerCase();
    if (!cleanEmail) {
      toast.error("Email is required");
      return;
    }
    if ((note || "").length > 500) {
      toast.error("Note must be 500 characters or fewer");
      return;
    }
    setSubmitting(true);
    resetCollisionState();
    try {
      const payload = {
        email: cleanEmail,
        name: name.trim() || undefined,
        role,
        store_id: storeId || null,
        department_id: departmentId || null,
        active,
        note: note.trim() || "",
      };
      const { data } = await api.post(
        `/platform/companies/${cid}/users`,
        payload,
      );
      setCreatedUser(data.user);
      toast.success("User created — minting one-time invite link…");
      await mintLink(data.user);
    } catch (err) {
      // Special-case the structured 409 from the duplicate-email guard
      // so we can render the v1 guidance callout instead of a plain toast.
      const d = err?.response?.data?.detail;
      const code =
        d && typeof d === "object" && typeof d.code === "string" ? d.code : null;
      if (err?.response?.status === 409 && code === "EMAIL_ALREADY_REGISTERED_SAME_TENANT") {
        // Same-tenant collision — switch to "Existing user" panel.
        if (d.existing_user && d.existing_user.id) {
          setExistingUser(d.existing_user);
        } else {
          // Backward-compat with older backend that returned no existing_user block.
          setDuplicateEmail(d);
        }
      } else if (
        err?.response?.status === 409 &&
        code &&
        code.startsWith("EMAIL_ALREADY_REGISTERED_")
      ) {
        setDuplicateEmail(d);
      } else {
        toast.error(formatApiErrorDetail(err));
      }
    } finally {
      setSubmitting(false);
    }
  };

  const mintLink = async (user) => {
    setMinting(true);
    try {
      const { data } = await api.post(
        `/platform/companies/${cid}/admin-password-reset`,
        { user_id: user.id },
      );
      setResetResult(data);
      if (data.prior_tokens_superseded > 0) {
        toast.success(
          `Fresh setup link minted (revoked ${data.prior_tokens_superseded} prior link${data.prior_tokens_superseded === 1 ? "" : "s"})`,
        );
      }
    } catch (err) {
      toast.error(formatApiErrorDetail(err));
    } finally {
      setMinting(false);
    }
  };

  const mintForExistingUser = async () => {
    if (!existingUser) return;
    setCreatedUser(existingUser);
    setResetResult(null);
    await mintLink(existingUser);
  };

  const copyReset = async () => {
    if (!resetResult?.reset_url) return;
    try {
      await navigator.clipboard.writeText(resetResult.reset_url);
      toast.success(
        resetResult.purpose === "invite"
          ? "Invite link copied to clipboard"
          : "Reset link copied to clipboard",
      );
    } catch {
      toast.error("Could not access clipboard — copy manually");
    }
  };

  const remint = async () => {
    if (!createdUser) return;
    setResetResult(null);
    await mintLink(createdUser);
  };

  const done = () => nav(`/platform/companies/${cid}`);

  // Helper for rendering existing-user assignment context.
  const storeName = (id) => stores.find((s) => s.id === id)?.name || "—";
  const deptName = (id) => departments.find((d) => d.id === id)?.name || "—";

  const isInvite = resetResult?.purpose === "invite";
  const linkLabel = isInvite ? "invite" : "reset";
  const linkLabelTitle = isInvite ? "Invite" : "Password reset";

  return (
    <div data-testid="activate-admin-page" className="max-w-2xl">
      <div className="mb-4">
        <Link
          to={`/platform/companies/${cid}`}
          className="inline-flex items-center gap-2 font-mono text-xs uppercase tracking-wider text-muted-foreground hover:text-ink"
          data-testid="activate-admin-back"
        >
          <ArrowLeft size={14} />
          Back to tenant
        </Link>
      </div>

      <PageHeader
        eyebrow={`Tenant · ${companyName || cid}`}
        title="Invite user"
        subtitle="Create a tenant user (admin, manager, or employee), then send them the one-time setup link to choose their own password."
      />

      {/* CROSS-TENANT collision — unchanged v1 guidance card. */}
      {duplicateEmail && (
        <Card
          variant="raised"
          className="p-5 mb-4 border-status-stale/40 bg-status-stale/5"
          data-testid="activate-admin-duplicate-callout"
        >
          <div className="flex items-start gap-3">
            <AlertTriangle
              size={20}
              className="text-status-stale shrink-0 mt-0.5"
            />
            <div className="flex-1 space-y-3">
              <div>
                <div className="font-display font-bold text-base uppercase tracking-wide text-status-stale">
                  Email already in use
                </div>
                <div className="font-mono text-sm text-ink mt-1">
                  <span data-testid="duplicate-callout-message">
                    {duplicateEmail.message}
                  </span>
                </div>
              </div>

              <div className="font-mono text-xs text-muted-foreground">
                {duplicateEmail.v1_guidance ||
                  "ShelfLife v1 uses one email per company tenant. You have two options:"}
              </div>

              <div className="grid sm:grid-cols-2 gap-3">
                <div
                  className="rounded-button border border-border/60 bg-paper-sunk p-3"
                  data-testid="duplicate-callout-option-different-email"
                >
                  <div className="flex items-center gap-2 mb-1">
                    <Mail size={14} className="text-ink shrink-0" />
                    <div className="font-display font-bold text-xs uppercase tracking-wide">
                      Use a different email
                    </div>
                  </div>
                  <div className="font-mono text-[11px] text-muted-foreground leading-relaxed">
                    Give this tenant&apos;s admin their own email. Plus-aliases
                    like{" "}
                    <span className="text-ink">
                      name+blueharbor@example.com
                    </span>{" "}
                    are RFC-compliant and route to the same inbox.
                  </div>
                </div>

                <div
                  className="rounded-button border border-border/60 bg-paper-sunk p-3"
                  data-testid="duplicate-callout-option-add-store"
                >
                  <div className="flex items-center gap-2 mb-1">
                    <Store size={14} className="text-ink shrink-0" />
                    <div className="font-display font-bold text-xs uppercase tracking-wide">
                      Add as a Store, not a Tenant
                    </div>
                  </div>
                  <div className="font-mono text-[11px] text-muted-foreground leading-relaxed">
                    If the same ownership group controls both locations,
                    add the new location as a <span className="text-ink">store</span>{" "}
                    under{" "}
                    <span className="text-ink">
                      {duplicateEmail.existing_tenant_name || "the existing tenant"}
                    </span>
                    . The existing admin manages both stores — no new
                    tenant needed.
                  </div>
                </div>
              </div>

              {duplicateEmail.existing_tenant_id && (
                <Link
                  to={`/platform/companies/${duplicateEmail.existing_tenant_id}`}
                  className="inline-flex items-center gap-2 font-mono text-xs uppercase tracking-wider text-ink hover:text-status-fresh underline"
                  data-testid="duplicate-callout-open-existing-tenant"
                >
                  <ArrowLeft size={12} />
                  Open existing tenant &lsquo;
                  {duplicateEmail.existing_tenant_name ||
                    duplicateEmail.existing_tenant_id}
                  &rsquo;
                </Link>
              )}
            </div>
          </div>
        </Card>
      )}

      {/* SAME-TENANT existing user — show details + mint-fresh CTA. */}
      {existingUser && !createdUser && (
        <Card
          variant="raised"
          className="p-5 mb-4"
          data-testid="existing-user-callout"
        >
          <div className="flex items-start gap-3 mb-3">
            <UserCheck size={20} className="text-brand shrink-0 mt-0.5" />
            <div className="flex-1">
              <div className="font-display font-bold text-base uppercase tracking-wide">
                User already exists in this tenant
              </div>
              <div className="font-mono text-xs text-muted-foreground mt-1">
                We won&apos;t create a duplicate. You can mint a fresh
                single-use setup link below — any prior unused links
                will be revoked automatically.
              </div>
            </div>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-4 text-sm">
            <div>
              <div className="data-sku mb-1">Email</div>
              <div
                className="font-display font-semibold truncate"
                data-testid="existing-user-email"
              >
                {existingUser.email}
              </div>
            </div>
            <div>
              <div className="data-sku mb-1">Name</div>
              <div className="font-mono text-xs">
                {existingUser.name || "—"}
              </div>
            </div>
            <div>
              <div className="data-sku mb-1">Role</div>
              <div
                className="font-mono text-xs uppercase"
                data-testid="existing-user-role"
              >
                {existingUser.role}
              </div>
            </div>
            <div>
              <div className="data-sku mb-1">Status</div>
              <UserStatusBadge
                active={!!existingUser.active}
                data-testid="existing-user-status"
              />
            </div>
            <div>
              <div className="data-sku mb-1">Store</div>
              <div className="font-mono text-xs truncate">
                {storeName(existingUser.store_id)}
              </div>
            </div>
            <div>
              <div className="data-sku mb-1">Department</div>
              <div className="font-mono text-xs truncate">
                {deptName(existingUser.department_id)}
              </div>
            </div>
          </div>

          <div className="flex gap-2 flex-wrap">
            <Button
              tier="primary"
              onClick={mintForExistingUser}
              disabled={minting}
              data-testid="existing-user-generate-setup-link"
            >
              <KeyRound size={14} />
              {existingUser.requires_setup
                ? "Generate setup link"
                : "Generate password reset link"}
            </Button>
            <Button
              tier="tertiary"
              onClick={() => {
                setExistingUser(null);
                setEmail("");
              }}
              disabled={minting}
              data-testid="existing-user-cancel"
            >
              Cancel
            </Button>
            <p className="font-mono text-[11px] text-muted-foreground basis-full mt-1">
              To change role / store / department, open the existing
              user from the Users list. This screen only mints links.
            </p>
          </div>
        </Card>
      )}

      {!createdUser && !existingUser ? (
        <Card variant="raised" className="p-6">
          <form className="space-y-4" onSubmit={createUser}>
            <div>
              <label className="data-sku block mb-1">Email *</label>
              <Input
                type="email"
                placeholder="user@example.com"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                autoFocus
                data-testid="activate-admin-email"
              />
            </div>
            <div>
              <label className="data-sku block mb-1">Full name</label>
              <Input
                placeholder="Jane Doe"
                value={name}
                onChange={(e) => setName(e.target.value)}
                data-testid="activate-admin-name"
              />
            </div>
            <div>
              <label className="data-sku block mb-1">Role *</label>
              <Select
                value={role}
                onChange={(e) => setRole(e.target.value)}
                data-testid="activate-admin-role"
              >
                <option value="employee">employee (default)</option>
                <option value="manager">manager</option>
                <option value="admin">admin</option>
              </Select>
              <p className="font-mono text-[11px] text-muted-foreground mt-1">
                Only tenant roles can be assigned here. Operator role
                can never be created via this surface.
              </p>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <div>
                <label className="data-sku block mb-1">Store (optional)</label>
                <Select
                  value={storeId}
                  onChange={(e) => setStoreId(e.target.value)}
                  data-testid="activate-admin-store"
                >
                  <option value="">— none —</option>
                  {stores.map((s) => (
                    <option key={s.id} value={s.id}>
                      {s.name}
                    </option>
                  ))}
                </Select>
              </div>
              <div>
                <label className="data-sku block mb-1">
                  Department (optional)
                </label>
                <Select
                  value={departmentId}
                  onChange={(e) => setDepartmentId(e.target.value)}
                  data-testid="activate-admin-department"
                >
                  <option value="">— none —</option>
                  {departments.map((d) => (
                    <option key={d.id} value={d.id}>
                      {d.name}
                    </option>
                  ))}
                </Select>
              </div>
            </div>

            <div>
              <label className="data-sku block mb-1">Note (optional)</label>
              <textarea
                value={note}
                onChange={(e) => setNote(e.target.value)}
                rows={2}
                maxLength={500}
                placeholder="Internal context — e.g. 'pilot tester at Aisle 5, contact via Slack #shelflife-pilot'"
                className="w-full px-3 py-2 bg-input border border-border rounded-button font-mono text-xs focus:outline-none focus:border-ink"
                data-testid="activate-admin-note"
              />
              <p
                className="font-mono text-[11px] text-muted-foreground mt-1"
                data-testid="activate-admin-note-counter"
              >
                {note.length}/500 — operator-only. Stored on the user
                doc and audit log; never shown to the invited user.
              </p>
            </div>

            <label
              className="flex items-center gap-2 cursor-pointer select-none"
              data-testid="activate-admin-active-label"
            >
              <input
                type="checkbox"
                checked={active}
                onChange={(e) => setActive(e.target.checked)}
                className="h-4 w-4 accent-brand"
                data-testid="activate-admin-active-checkbox"
              />
              <span className="font-mono text-xs">
                Active on creation
                <span className="text-muted-foreground">
                  {" "}
                  · uncheck to stage an inactive user (no login until
                  manually activated)
                </span>
              </span>
            </label>

            <div className="flex gap-2 pt-2">
              <Button
                tier="primary"
                type="submit"
                disabled={submitting || !email.trim()}
                data-testid="activate-admin-submit"
              >
                {submitting ? "Creating…" : "Create user & mint setup link"}
              </Button>
              <Button
                tier="tertiary"
                type="button"
                onClick={done}
                disabled={submitting}
                data-testid="activate-admin-cancel"
              >
                Cancel
              </Button>
            </div>
          </form>
        </Card>
      ) : null}

      {createdUser && (
        <Card variant="raised" className="p-6">
          <div className="flex items-center gap-2 mb-4 text-status-fresh">
            <CheckCircle2 size={20} />
            <span
              className="font-display font-bold text-sm uppercase tracking-wide"
              data-testid="activate-admin-success"
            >
              {isInvite ? "User invited" : "Link ready"}
            </span>
          </div>
          <div className="space-y-3 mb-4">
            <div>
              <div className="data-sku mb-1">Email</div>
              <div className="font-display font-semibold">
                {createdUser.email}
              </div>
            </div>
            <div>
              <div className="data-sku mb-1">Role</div>
              <div className="font-mono text-sm uppercase">
                {createdUser.role}
              </div>
            </div>
          </div>

          <div className="border-t border-border/40 pt-4">
            <div className="flex items-center gap-2 mb-2 text-ink">
              <KeyRound size={16} />
              <span className="font-display font-bold text-sm uppercase tracking-wide">
                One-time {linkLabel} link
              </span>
            </div>
            {minting && (
              <div className="font-mono text-sm text-muted-foreground">
                Minting link…
              </div>
            )}
            {resetResult && (
              <>
                {resetResult.delivery === "emailed" ? (
                  <div
                    className="flex items-start gap-2 bg-status-fresh/10 border border-status-fresh/30 text-status-fresh px-3 py-2 rounded-button mb-3"
                    data-testid="activate-admin-delivery-emailed"
                  >
                    <Mail size={16} className="mt-0.5 shrink-0" />
                    <div>
                      <div className="font-display font-bold text-sm uppercase tracking-wide">
                        {linkLabelTitle} emailed to{" "}
                        <span className="text-ink">{createdUser?.email}</span>
                      </div>
                      <div className="font-mono text-[11px] text-muted-foreground mt-0.5">
                        Sent via Resend
                        {resetResult.email_message_id ? (
                          <>
                            {" "}· id{" "}
                            <span className="text-ink">
                              {resetResult.email_message_id}
                            </span>
                          </>
                        ) : null}
                        . Backup link below in case it gets caught by spam.
                      </div>
                    </div>
                  </div>
                ) : (
                  <div
                    className="flex items-start gap-2 bg-amber-50 border border-amber-300 text-amber-900 px-3 py-2 rounded-button mb-3"
                    data-testid="activate-admin-delivery-manual"
                  >
                    <Mail size={16} className="mt-0.5 shrink-0" />
                    <div>
                      <div className="font-display font-bold text-sm uppercase tracking-wide">
                        Email delivery not configured
                      </div>
                      <div className="font-mono text-[11px] text-amber-900/80 mt-0.5">
                        Copy the link below and send it to{" "}
                        <span className="text-ink">{createdUser?.email}</span>{" "}
                        manually.
                      </div>
                    </div>
                  </div>
                )}
                {resetResult.prior_tokens_superseded > 0 && (
                  <div
                    className="flex items-start gap-2 bg-paper-sunk border border-border/60 text-ink px-3 py-2 rounded-button mb-3"
                    data-testid="activate-admin-prior-superseded"
                  >
                    <RotateCcw size={14} className="mt-0.5 shrink-0" />
                    <div className="font-mono text-[11px]">
                      Revoked{" "}
                      <span className="text-ink">
                        {resetResult.prior_tokens_superseded}
                      </span>{" "}
                      prior unused link
                      {resetResult.prior_tokens_superseded === 1 ? "" : "s"} for
                      this user. Only the link below is now valid.
                    </div>
                  </div>
                )}
                <textarea
                  readOnly
                  value={resetResult.reset_url}
                  className="w-full font-mono text-[11px] p-3 bg-paper-sunk rounded-button border border-border/60 break-all"
                  rows={4}
                  data-testid="activate-admin-reset-url"
                  onClick={(e) => e.target.select()}
                />
                <p className="font-mono text-[11px] text-muted-foreground mt-1">
                  Expires{" "}
                  <span className="text-ink">{resetResult.expires_at}</span>.
                  Single-use; once consumed it cannot be replayed.
                </p>
                <div className="flex gap-2 mt-3">
                  <Button
                    tier="primary"
                    onClick={copyReset}
                    data-testid="activate-admin-copy"
                  >
                    Copy link
                  </Button>
                  <Button
                    tier="secondary"
                    onClick={remint}
                    disabled={minting}
                    data-testid="activate-admin-remint"
                  >
                    Re-mint
                  </Button>
                  <Button
                    tier="tertiary"
                    onClick={done}
                    data-testid="activate-admin-done"
                    className="ml-auto"
                  >
                    Done
                  </Button>
                </div>
              </>
            )}
          </div>
        </Card>
      )}
    </div>
  );
}
